<template>
  <div>
    <template v-if="unrelatedFields.length">
      <template v-if="isModal || isFilePreviewer">
        <template v-for="item of transformedFields">
          <div :id="`addEditRowModalFieldContainer_${item.name}`" :key="item.fieldGuid">
            <input-widget
              v-if="item.displayInForm && item.dataTypeName !== 'lookup'"
              :id="`addEditRowModalField_${item.name}`"
              :key="item.fieldGuid"
              :model="isEditMode ? dataInEditMode : defaultForm"
              class="field"
              :field="item"
              :rls="item.isRLSField"
              :disabled="item.disabled || item.currentUserPermissionLevel === 'read' || isCalculatedLookup(item) || isAiCalculation(item)"
              :skip-validation-rules="false"
              :date-format="getDateFormat(item)"
            />
            <!-- @on-add-new-relational-entity="onAddNewRelationalEntity" -->
            <lookup-input-component
              v-else-if="item.displayInForm && item.dataTypeName === 'lookup'"
              transit-ref="lkp-multiselect"
              :is-label-visible="true"
              :is-edit-modal="true"
              :disabled="item.disabled || item.currentUserPermissionLevel === 'read' || isCalculatedLookup(item)"
              :field="item"
              :props-value="isEditMode ? dataInEditMode[item.name] : defaultForm[item.name]"
              :model="isEditMode ? dataInEditMode : defaultForm"
              @on-select="selectLkpValue"
              @on-add-new-relation="onAddNewRelationalEntity"
            />
          </div>
        </template>
      </template>

      <div v-else>
        <!--            @dragend="dragend({e: $event, uuid: item.fieldGuid})"-->
        <!--            @drag="drag({e: $event, uuid: item.fieldGuid})"-->
        <div
          v-for="item of fieldsForRender"
          :key="item.fieldGuid"
          class="block__item"
        >
          <!--          <div class="draggable-handle">-->
          <!--            <font-awesome-icon :icon="['fas', 'bars']"/>-->
          <!--          </div>-->
          <span
            class="field__name"
            v-html="formattedItemName(item.displayName)"
          />

          <b-dropdown
            no-caret
            variant="outlined"
            :disabled="!data.Sections.length"
            class="additional-options"
          >
            <template #button-content>
              <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
            </template>
            <b-dropdown-text>Move to:</b-dropdown-text>
            <b-dropdown-divider />
            <b-dropdown-item
              v-for="section of data.Sections"
              :key="section.id"
              @click="moveItemToSection(section.id, item.fieldGuid)"
            >
              {{ section.Name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </template>
    <div
      v-if="!isModal"
      class="empty__wrapper"
    >
      <no-content
        text="You have no fields available for allocation"
        :show="!unrelatedFields.length"
      />
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import InputWidget from '@/core/components/complex-table/components/InputWidget.vue';
import NoContent from '@/core/components/NoContent.vue';
import moment from 'moment';
import {highlightText} from '@/core/utils';
import LookupInputComponent from '@/core/components/LookupInputComponent.vue';

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');

const {
  mapState: designerMapState,
  mapActions: designerMapActions,
} = createNamespacedHelpers('designer');

export default {
  name: 'UnrelatedFields',
  components: {
    LookupInputComponent,
    NoContent,
    InputWidget,
  },
  props: {
    searchValue: {
      type: String,
      default: '',
    },
    isFilePreviewer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localDataInEditMode: {},
      DragPos: {
        row: null,
        column: null,
        width: 1,
        height: 1,
        index: null,
      },
      transformedFields: null,
      currentLayout: null,
      container: null,
      copy: null,
      newPosition: null,
      mouseInGrid: false,
    };
  },
  watch: {
    unrelatedFields: {
      handler() {
        this.copy = JSON.parse(JSON.stringify(this.unrelatedFields));
      },
      deep: true,
      immediate: true,
    },
    sectionId: {
      handler(_, old) {
        if (!old) return;
        const list = this.currentLayout.Fields.filter(obj => obj.i !== 'drop');
        this.updateCurrentLayoutList({id: old, list});
        this.parentRefs[old].dragEvent('dragend', 'drop', this.newPosition.x, this.newPosition.y, 1, 3);
      },
    },
  },
  computed: {
    ...designerMapState({
      data: state => state.data,
      unrelatedFields: state => state.unrelatedFields,
      defaultForm: state => state.form,
      dataInEditMode: state => state.item,
      isEditMode: state => state.isEditMode,
      isDesignerSidebarOpened: state => state.isDesignerSidebarOpened,
      mouseXY: state => state.mouseXY,
      sectionId: state => state.dndSectionId,
      parentRefs: state => state.parentRefs,
      isModal: state => state.isModalOpened,
      fields: state => state.fields,
    }),
    ...dataManagementMapState({
      fieldsTypesValues: state => state.fieldValues,
      tableSchema: state => state.table.schema,
      permissionTypes: state => state.permissionTypes,
    }),
    isPostedStatusExist() {
      const postedValue = this.unrelatedFields.find(el => el.fieldGuid === this.tableSchema.postedStatusGuid);
      return this.isEditMode ? this.dataInEditMode[postedValue?.name] === 'Posted' : false;
    },
    fieldsForRender() {
      if (this.searchValue && this.searchValue.length) {
        return this.copy.filter(el => el.displayName.toLowerCase().includes(this.searchValue.toLowerCase()));
      }
      return this.unrelatedFields;
    },
  },
  methods: {
    ...dataManagementMapActions(['showAddNewRecordModal']),
    ...designerMapActions([
      'updateCurrentLayout',
      'updateCurrentLayoutList',
      'resetDndSectionId',
      'removeFromUnrelatedFields',
      'moveItemToAnotherSection',
      'updateModel',
    ]),
    selectLkpValue(field, selectedValue){
      const data = this.isEditMode
        ? this.dataInEditMode
        : this.defaultForm;

      data[field.name] = selectedValue;
    },
    formattedItemName(name) {
      return highlightText(this.searchValue || '', name);
    },
    moveItemToSection(toSectionId, fieldId) {
      this.moveItemToAnotherSection({
        toSectionId,
        fromSectionId: null,
        fieldId,
      });
    },
    // resetDragPosition() {
    //   this.DragPos = {
    //     row: null,
    //     column: null,
    //     width: 1,
    //     height: 1,
    //     index: null
    //   }
    // },
    async onAddNewRelationalEntity(item, index, field) {
      // let fields = this.modal.fields || this.fields
      const primary = this.tableSchema.fields.find((f) => f.isPrimaryKey);

      const currentData = this.isEditMode ?   this.dataInEditMode : this.defaultForm;

      const params = {
        inline: true,
        record: {},
        todo: false,
        relationalTableId: field.relationalTableId,
        field: field,
        parentKey: this.modalKey,
        parentData: {...this.form},
        recordUuid: currentData[primary.name],
        entityUuid: field.entityGuid,
        defaultLookupValue: this.getLookupDefaultValue(currentData, field, true),
      };
      this.showAddNewRecordModal(params);
    },
    getLookupDefaultValue(item, field, isRelational = false) {
      if (field !== undefined) {
        const suggestionName = item[`${field.name}__$LookupStatus`];
        let lookupDefaultValue;
        const relationalField = field.data.lookup;
        if (item.customClass && this.isValidSuggestions(suggestionName)) {
          const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
          lookupDefaultValue = suggestions[0].OriginalValue || suggestions[0].originalValue;
        }
        return isRelational ? {[relationalField.value]: lookupDefaultValue} : {[field.name]: lookupDefaultValue};
      }
    },
    isCalculatedLookup(field) {
      if (field.dataTypeName === 'lookup') {
        let dataTypeJSON;

        if (field.dataTypeJSON) {
          dataTypeJSON = JSON.parse(field.dataTypeJSON);
        }

        return dataTypeJSON.IsCalculated;
      }
    },
    isAiCalculation(field) {
      return !!(field.dataTypeName === 'text' && field.calculation && field.calculation.fieldCalculationType === 'AIChat');
    },
    getDateFormat(field) {

      const jsonData = typeof field.dataTypeJSON === 'string'
        ? JSON.parse(field.dataTypeJSON)
        :field.dataTypeJSON;
      const isDateField = field.dataTypeName === this.fieldsTypesValues.date;
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';

      return `${format}${isDateField ? '' : ' HH:mm:ss'}`;
    },
    // getContainer() {
    //   const $container = document.getElementById(this.sectionId)
    //   return {
    //     isContainerExist: $container,
    //     container: $container
    //   }
    // },
    // drag: throttle(async function (e) {
    //   const {isContainerExist, container} = this.getContainer()
    //
    //   if (!isContainerExist) return
    //
    //   const {left, bottom, right, top} = container.getBoundingClientRect();
    //   const {x, y} = this.mouseXY
    //
    //   let mouseInGrid = false;
    //   if (((x > left) && (x < right)) && ((y > top) && (y < bottom))) {
    //     mouseInGrid = true;
    //   }
    //
    //   const currentLayout = this.data.Sections.find(el => el.id === this.sectionId)
    //   this.currentLayout = currentLayout
    //
    //   if (mouseInGrid && (currentLayout.Fields.findIndex(item => item.i === 'drop')) === -1) {
    //     if (currentLayout) {
    //       await this.updateCurrentLayout({
    //         layoutId: this.sectionId,
    //         newLayoutItem: {
    //           x: (currentLayout.Fields.length * 2) % 12,
    //           y: currentLayout.Fields.length + 12, // puts it at the bottom
    //           w: 3,
    //           h: 1,
    //           i: 'drop',
    //           fieldGuid: e.uuid,
    //         }
    //       })
    //     }
    //   }
    //   let index = currentLayout.Fields.findIndex(item => item.i === 'drop');
    //   if (index !== -1) {
    //     try {
    //       this.container.children[currentLayout.length].style.display = "none";
    //     } catch {
    //     }
    //
    //     let el = this.parentRefs[this.sectionId].$children[index];
    //     let new_pos
    //
    //     if (!('calcXY' in el) && !el.$children.length) {
    //       return;
    //     } else {
    //       el.dragging = {
    //         top: y - top,
    //         left: x - left
    //       };
    //       new_pos = 'calcXY' in el
    //           ? el.calcXY(y - top, x - left)
    //           : el.$children[0].calcXY(y - top, x - left);
    //     }
    //
    //     this.newPosition = new_pos
    //
    //     if (mouseInGrid) {
    //       await this.parentRefs[this.sectionId].dragEvent('dragstart', 'drop', new_pos.x, new_pos.y, 1, 3);
    //       this.DragPos.index = String(currentLayout.Fields.length);
    //       this.DragPos.row = currentLayout.Fields[index].x;
    //       this.DragPos.column = currentLayout.Fields[index].y;
    //     }
    //   }
    // }, 100),
    // async dragend(e) {
    //
    //   const {isContainerExist, container} = this.getContainer()
    //   if (!isContainerExist) return
    //
    //   const {left, top, right, bottom} = container.getBoundingClientRect();
    //   const {x, y} = this.mouseXY
    //   let mouseInGrid = false;
    //
    //   if (((x > left) && (x < right)) && ((y > top) && (y < bottom))) {
    //     mouseInGrid = true;
    //   }
    //   if (mouseInGrid) {
    //
    //     await this.parentRefs[this.sectionId].dragEvent('dragend', 'drop', this.DragPos.row, this.DragPos.column, 1, 1);
    //     const currentLayout = this.data.Sections.find(el => el.id === this.sectionId)
    //     this.currentLayout = currentLayout
    //     const list = currentLayout.Fields.filter(obj => obj.i !== 'drop');
    //
    //     this.updateCurrentLayoutList({id: this.sectionId, list})
    //
    //     this.updateCurrentLayout({
    //       layoutId: this.sectionId,
    //       newLayoutItem: {
    //         x: this.DragPos.row,
    //         y: this.DragPos.column,
    //         w: 3,
    //         h: 1,
    //         i: this.DragPos.index,
    //         fieldGuid: e.uuid,
    //       }
    //     })
    //
    //     this.parentRefs[this.sectionId].dragEvent(
    //         'dragend',
    //         currentLayout.Fields.length + 1,
    //         this.DragPos.row,
    //         this.DragPos.column,
    //         3,
    //         1
    //     );
    //     this.removeFromUnrelatedFields(e.uuid)
    //     this.resetDragPosition()
    //     this.resetDndSectionId()
    //     try {
    //       this.parentRefs[this.sectionId].$children[currentLayout.length].$refs.item.style.display = "block";
    //     } catch {
    //     }
    //   }
    // },
  },
  mounted() {
    const copy = JSON.parse(JSON.stringify(this.dataInEditMode));
    if (this.isEditMode) {
      this.localDataInEditMode = JSON.parse(JSON.stringify(this.dataInEditMode));
    }
    if (!this.unrelatedFields) return;
    this.transformedFields = this.unrelatedFields.map(el => {
      if (this.permissionTypes.deny === el.currentUserPermissionLevel) {
        return false;
      }

      const jsonData = typeof el.dataTypeJSON === 'string'
        ? JSON.parse(el.dataTypeJSON)
        : el.dataTypeJSON;

      el.disabled = this.isPostedStatusExist;
      el.label = el.displayName;
      if (el.dataTypeName === 'decimalNumber'
          || el.dataTypeName === 'floatNumber'
          || el.dataTypeName === 'wholeNumber') {
        el.type = 'number';
        if (jsonData.IsPercentage) {
          el.placeholder = `${el.label}%`;
        }
      } else if (el.dataTypeName === 'dateTime') {
        el.type = 'datetime';
        el.placeholder = this.getDateFormat(el);
        if (this.isEditMode) {
          // this.updateModel({
          //   key: el.label,
          //   value: !this.dataInEditMode[el.label]
          //       ? ''
          //       : moment(this.dataInEditMode[el.label]).format(this.getDateFormat(el))}
          // )
          // this.localDataInEditMode[el.name] = !this.dataInEditMode[el.name]
          //   ? ''
          //   : moment(this.dataInEditMode[el.name]).format(this.getDateFormat(el));
          this.dataInEditMode[el.name] = !this.dataInEditMode[el.name]
            ? ''
            : moment(this.dataInEditMode[el.name]).format(this.getDateFormat(el));
        }
      } else if (el.dataTypeName === 'date') {
        el.type = 'date';
        el.placeholder = this.getDateFormat(el);
        if (this.isEditMode) {
          // this.updateModel({
          //   key: el.label,
          //   value: !this.dataInEditMode[el.label]
          //       ? ''
          //       : moment(this.dataInEditMode[el.label]).format(this.getDateFormat(el))}
          // )
          // this.localDataInEditMode[el.name] = !this.dataInEditMode[el.name]
          //   ? ''
          //   : moment(this.dataInEditMode[el.name]).format(this.getDateFormat(el));
          this.dataInEditMode[el.name] = !this.dataInEditMode[el.name]
            ? ''
            : moment(this.dataInEditMode[el.name]).format(this.getDateFormat(el));
        }
      } else if (el.dataTypeName === 'lookup') {

        const additionalData = this.tableSchema.fields.find(item => item.fieldGuid === el.fieldGuid);
        if (this.isEditMode) {
          const currentLookupValue = additionalData.data.options.find(item => {
            return item.id === this.dataInEditMode[el.name];
          });
          // console.log(el.label.split(' ').join(''))
          // console.log({
          //   key: el.label.split(' ').join(''),
          //   value: currentLookupValue
          // }, this.dataInEditMode)
          //
          // this.updateModel({
          //   key: el.label.split(' ').join(''),
          //   value: currentLookupValue
          // })
          // this.localDataInEditMode[el.name] = currentLookupValue;
          this.dataInEditMode[el.name] = currentLookupValue;
        }
        el.type = 'multi-select';
        el.config = additionalData.config;
        el.data = additionalData.data;
        el.relationalFieldId = additionalData.relationalFieldId;
        el.relationalTableId = additionalData.relationalTableId;
        el.trackChanges = additionalData.trackChanges;
        el.isTable = true;
      } else if (el.dataTypeName === 'boolean') {
        el.type = 'checkbox';
      } else if (this.tableSchema.postedStatusGuid === el.fieldGuid && el.dataTypeName === 'text') {
        el.type = 'select';
        el.disabled = !['admin', 'edit', 'unpost', 'post'].includes(this.tableSchema.currentUserPermissionLevel);
        if (!this.defaultForm[el.name]) {
          this.defaultForm[el.name] = 'Pending';
        }
        return el;
      }
      return {...el, disabled: this.isPostedStatusExist};
    });
  },

};
</script>

<style scoped lang="scss">
.empty__wrapper,
.block__item {
  position: relative;
  //cursor: grab;

  .field__name {
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.empty__wrapper {
  color: #ccc;
  text-align: center;
  font-size: 15px;
}

.block__item,
.draggable-handle {
  display: flex;
  align-items: center;
  justify-content: center;
}

.block__item {
  border-radius: 6px;
  border: 2px solid #eee;
  height: 100%;
  padding: 10px;

  &:not(:last-child) {
    margin-bottom: 10px;
  }
}

.draggable-handle,
.additional-options {
  position: absolute;
  top: 0;
}

.draggable-handle {
  width: 40px;
  height: 40px;
  left: 0;
}

.additional-options {
  cursor: pointer;
  top: 50%;
  transform: translateY(-50%);
  right: 0;

  .separator {
    margin: 0;
  }

  &:has(.show) {
    z-index: 10;
  }

  & :disabled {
    cursor: not-allowed;
  }
}
</style>
